import React from 'react';

import NextLink from '../NextLink';

import ArrowForward from '@src/components/Icons/ArrowForward';

import { ButtonPropTypes } from './Button.types';
import InspectorWrapper from '../InspectorWrapper';

const Button: React.FC<ButtonPropTypes> = ({
  entryId = '',
  attr,
  containerClassName = '',
  btnClassName = '',
  btnText,
  primary,
  secondary,
  tertiary,
  quaternary, // Used for download files
  href,
  align = 'justify-center',
  handleOnClick,
  disabled,
  target = '_self',
  svgClassName = '',
  locale,
}) => {
  const localeOverwrite = locale && locale !== 'en' ? `/${locale}${href}` : href;

  return (
    <InspectorWrapper entryId={entryId} fieldId='text'>
      <div className={`flex items-center ${align} ${containerClassName}`}>
        {primary && !href && (
          <button
            data-test={attr}
            onClick={handleOnClick}
            title={disabled ? 'Button is disabled' : btnText}
            style={{
              textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
            }}
            className={`bg-primary h-[4.4rem] rounded-[0.6rem] px-[2rem] text-center text-[1.6rem] leading-[1.8rem] font-normal tracking-[-0.0304rem] text-[#FFFFFF] transition-colors duration-300 ease-in-out ${btnClassName} ${
              disabled
                ? 'bg-background-primary! pointer-events-none cursor-not-allowed border-[0.1rem] border-[#B9B9B9] text-[#B9B9B9]!'
                : 'common-btn-transition hover:bg-primary--v2'
            }`}
          >
            {btnText}
          </button>
        )}

        {primary && href && (
          <NextLink
            data-test={attr}
            href={localeOverwrite as string}
            title={disabled ? 'Button is disabled' : btnText}
            style={{
              textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
            }}
            className={`bg-primary flex h-[4.4rem] items-center justify-center rounded-[0.6rem] px-[2rem] text-center text-[1.6rem] leading-[1.6rem] font-normal tracking-[-0.0304rem] text-[#FFFFFF] transition-colors duration-300 ease-in-out ${btnClassName} ${
              disabled
                ? 'bg-background-primary! pointer-events-none cursor-not-allowed border-[0.1rem] border-[#B9B9B9] text-[#B9B9B9]!'
                : 'common-btn-transition hover:bg-primary--v2'
            }`}
          >
            {btnText}
            {btnText === 'Learn more' && (
              <span className='screen-reader-text'>Please click here to learn more about our products</span>
            )}
          </NextLink>
        )}

        {secondary && !href && (
          <button
            data-test={attr}
            data-pseudo={btnText}
            onClick={handleOnClick}
            title={disabled ? 'Button is disabled' : btnText}
            style={{
              textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
            }}
            className={`border-primary text-primary before:text-primary hover:text-primary--v2! hover:before:text-primary--v2 h-[4.4rem] rounded-[0.6rem] border-[0.1rem] bg-[#FFFFFF] px-[2rem] text-center text-[1.6rem] leading-[1.6rem] tracking-[-0.0304rem] transition-colors duration-300 ease-in-out before:w-full before:px-[2rem] before:text-center before:font-normal before:content-[attr(data-pseudo)] ${btnClassName} ${
              disabled
                ? 'bg-background-primary! pointer-events-none cursor-not-allowed border-[0.1rem] border-[#B9B9B9]! text-[#B9B9B9]!'
                : 'common-btn-transition hover:border-primary--v2 hover:text-primary--v2'
            }`}
          >
            {btnText}
          </button>
        )}

        {secondary && href && (
          <NextLink
            data-test={attr}
            dataPseudo={btnText}
            href={localeOverwrite as string}
            title={disabled ? 'Button is disabled' : btnText}
            className={`border-primary before:text-primary hover:text-primary--v2! hover:before:text-primary--v2 relative bottom-[0.1rem] flex h-[4.4rem] items-center justify-center rounded-[0.6rem] border-[0.1rem] px-[2rem] text-center text-[1.6rem] leading-[1.6rem] font-normal tracking-[-0.0304rem] text-[#793300]! transition-colors duration-300 ease-in-out before:absolute before:z-20 before:w-full before:px-[2rem] before:text-center before:font-normal before:content-[attr(data-pseudo)]! ${btnClassName} ${
              disabled
                ? 'bg-background-primary! pointer-events-none cursor-not-allowed border-[0.1rem] border-[#B9B9B9]! text-[#B9B9B9]!'
                : 'common-btn-transition hover:border-primary--v2 hover:text-primary--v2'
            }`}
          >
            {btnText}
            {btnText === 'Learn more' && (
              <span className='screen-reader-text'>Please click here to learn more about our products</span>
            )}
          </NextLink>
        )}

        {tertiary && href && (
          <NextLink
            data-test={attr}
            href={localeOverwrite as string}
            title={disabled ? 'Button is disabled' : btnText}
            className={`common-btn-transition text-primary hover:text-primary--v2 flex items-center rounded-[0.6rem] bg-transparent text-[1.6rem] leading-[1.6rem] font-normal tracking-[-0.0304rem] transition-colors duration-300 ease-in-out ${btnClassName} ${
              disabled
                ? 'pointer-events-none cursor-not-allowed bg-transparent! text-[#B9B9B9]!'
                : 'common-btn-transition hover:text-primary--v2'
            }`}
          >
            <span
              style={{ textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px' }}
              className={`mr-[0.8rem]`}
            >
              {btnText}{' '}
              {btnText === 'Learn more' && (
                <span className='screen-reader-text'>Please click here to learn more about our products</span>
              )}
            </span>
            <ArrowForward
              className={`text-primary hover:text-primary--v2 mt-[0.1rem] h-[1.4rem] w-[0.646rem] ${svgClassName} ${
                disabled ? 'pointer-events-none text-[#B9B9B9]!' : 'common-btn-transition'
              }`}
            />
          </NextLink>
        )}

        {quaternary && href && (
          <a
            data-test={attr}
            href={localeOverwrite as string}
            title={disabled ? 'Button is disabled' : btnText}
            target={target}
            className={`bg-primary flex h-[4.4rem] items-center justify-center rounded-[0.6rem] px-[2rem] text-[1.6rem] leading-[1.6rem] tracking-[-0.0304rem] text-[#FFFFFF] transition-colors duration-300 ease-in-out ${btnClassName} ${
              disabled
                ? 'bg-background-primary! pointer-events-none cursor-not-allowed border-[0.1rem] border-[#B9B9B9] text-[#B9B9B9]!'
                : 'common-btn-transition hover:bg-primary--v2'
            }`}
          >
            {btnText}
            {btnText === 'Learn more' && (
              <span className='screen-reader-text'>Please click here to learn more about our products</span>
            )}
          </a>
        )}
      </div>
    </InspectorWrapper>
  );
};

export default Button;
